import apiClient from '../client';
import {
  Vehicle,
  VehiclesResponse,
  VehicleApplication,
  VehicleLookupData,
  VehicleExportDto
} from '../../../../shared/types';

export const vehicleService = {
  /**
   * Get all vehicles with optional make filter
   */
  async getVehicles(makeId?: number, modelId?: number, page: number = 1, partType?: string, includeEmpty?: boolean): Promise<VehiclesResponse> {
    const params: Record<string, string | number> = { page };
    if (makeId) params.makeId = makeId;
    if (modelId) params.modelId = modelId;
    if (partType) params.partType = partType;
    if (includeEmpty) params.includeEmpty = 'true';
    const response = await apiClient.get<VehiclesResponse>('/vehicles', { params });
    return response.data;
  },

  /**
   * Get a single vehicle by ID
   */
  async getVehicleById(id: number): Promise<Vehicle> {
    const response = await apiClient.get<Vehicle>(`/vehicles/${id}`);
    return response.data;
  },

  /**
   * Create a new vehicle
   */
  async createVehicle(vehicle: Vehicle): Promise<{ id: number; message: string }> {
    const response = await apiClient.post<{ id: number; message: string }>('/vehicles', vehicle);
    return response.data;
  },

  /**
   * Update an existing vehicle
   */
  async updateVehicle(id: number, vehicle: Vehicle): Promise<{ message: string }> {
    const response = await apiClient.put<{ message: string }>(`/vehicles/${id}`, vehicle);
    return response.data;
  },

  /**
   * Delete a vehicle
   */
  async deleteVehicle(id: number): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(`/vehicles/${id}`);
    return response.data;
  },

  /**
   * Search vehicles by part number
   */
  async searchByPartNumber(partNumber: string): Promise<VehiclesResponse> {
    const response = await apiClient.get<VehiclesResponse>('/vehicles/search', {
      params: { partNumber }
    });
    return response.data;
  },

  /**
   * Get applications for a vehicle
   */
  async getApplications(vehicleId: number): Promise<VehicleApplication[]> {
    const response = await apiClient.get<VehicleApplication[]>(`/vehicles/${vehicleId}/applications`);
    return response.data;
  },

  /**
   * Look up a PartApplication by part number — returns { Id, PartNumber }
   */
  async getPartApplicationByPartNumber(partNumber: string): Promise<{ Id: number; PartNumber: string } | null> {
    const response = await apiClient.get<{ Id: number; PartNumber: string }>('/vehicles/parts/by-number', {
      params: { partNumber },
      validateStatus: (status) => status === 200 || status === 404,
    });
    if (response.status === 404) return null;
    return response.data;
  },

  /**
   * Get a single PartApplication with all editable fields + QAs
   */
  async getPartApplication(partId: number): Promise<any> {
    const response = await apiClient.get(`/vehicles/parts/${partId}`);
    return response.data;
  },

  /**
   * Update a PartApplication's editable fields
   */
  async updatePartApplication(partId: number, data: any): Promise<void> {
    await apiClient.put(`/vehicles/parts/${partId}`, data);
  },

  /**
   * Get lookup data for the Edit Application modal
   */
  async getApplicationLookupData(): Promise<any> {
    const response = await apiClient.get('/vehicles/application-lookup');
    return response.data;
  },

  /**
   * Add a question-answer pair to a PartApplication
   */
  async addApplicationQuestion(partId: number, questionId: number, answerId: number): Promise<number> {
    const response = await apiClient.post<{ id: number }>(`/vehicles/parts/${partId}/questions`, { questionId, answerId });
    return response.data.id;
  },

  /**
   * Delete a question-answer pair
   */
  async deleteApplicationQuestion(partId: number, qaId: number): Promise<void> {
    await apiClient.delete(`/vehicles/parts/${partId}/questions/${qaId}`);
  },

  /**
   * Add a part application link to a vehicle by part number
   */
  async addApplication(vehicleId: number, partNumber: string): Promise<{ partId: number }> {
    const response = await apiClient.post<{ partId: number }>(`/vehicles/${vehicleId}/applications`, { partNumber });
    return response.data;
  },

  /**
   * Create a new PartApplication record and link it to a vehicle
   */
  async createPartApplication(vehicleId: number, data: {
    partNumber: string;
    seatTypeId?: number | null;
    seatRowCode?: string;
    priceCode?: string;
    costCode?: string;
    fitmentCode?: string;
    extras?: string;
    notes?: string;
  }): Promise<{ partId: number }> {
    const response = await apiClient.post<{ partId: number }>(`/vehicles/${vehicleId}/applications/new`, data);
    return response.data;
  },

  /**
   * Remove a part application link from a vehicle
   */
  async removeApplication(vehicleId: number, partId: number): Promise<void> {
    await apiClient.delete(`/vehicles/${vehicleId}/applications/${partId}`);
  },

  /**
   * Get all lookup data for forms
   */
  async getLookupData(): Promise<VehicleLookupData> {
    const response = await apiClient.get<VehicleLookupData>('/vehicles/lookup-data');
    return response.data;
  },

  /**
   * Export vehicles to Excel
   */
  async exportToExcel(makeIds: number[]): Promise<Blob> {
    const response = await apiClient.post('/vehicles/export', { makeIds }, {
      responseType: 'blob'
    });
    return response.data;
  },

  /**
   * Get print parts list data
   */
  async getPrintPartsListData(makeIds: number[]): Promise<VehicleExportDto[]> {
    const response = await apiClient.post<VehicleExportDto[]>('/vehicles/print-parts-list', { makeIds });
    return response.data;
  },

  async exportCargoToExcel(makeIds: number[]): Promise<Blob> {
    const response = await apiClient.post('/vehicles/cargo/export', { makeIds }, { responseType: 'blob' });
    return response.data;
  },

  async getPrintCargoPartsListData(makeIds: number[]): Promise<VehicleExportDto[]> {
    const response = await apiClient.post<VehicleExportDto[]>('/vehicles/cargo/print-parts-list', { makeIds });
    return response.data;
  },

  // ── App Finder cascade ──────────────────────────────────────────────────────

  async getAppFinderYears(): Promise<number[]> {
    const response = await apiClient.get<number[]>('/vehicles/app-finder/years');
    return response.data;
  },

  async getAppFinderMakes(year: number): Promise<{ Id: number; Name: string }[]> {
    const response = await apiClient.get<{ Id: number; Name: string }[]>('/vehicles/app-finder/makes', { params: { year } });
    return response.data;
  },

  async getAppFinderModels(year: number, makeId: number): Promise<{ Id: number; Name: string }[]> {
    const response = await apiClient.get<{ Id: number; Name: string }[]>('/vehicles/app-finder/models', { params: { year, makeId } });
    return response.data;
  },

  async getAppFinderVehicleOptions(year: number, makeId: number, modelId: number): Promise<{ Id: number; Name: string }[]> {
    const response = await apiClient.get<{ Id: number; Name: string }[]>('/vehicles/app-finder/options', { params: { year, makeId, modelId } });
    return response.data;
  },

  async getAppFinderPartsData(vehicleId: number): Promise<any> {
    const response = await apiClient.get(`/vehicles/${vehicleId}/app-finder`);
    return response.data;
  },

  // ── Cargo App Finder cascade ────────────────────────────────────────────────

  async getCargoAppFinderYears(): Promise<number[]> {
    const response = await apiClient.get<number[]>('/vehicles/cargo/app-finder/years');
    return response.data;
  },

  async getCargoAppFinderMakes(year: number): Promise<{ Id: number; Name: string }[]> {
    const response = await apiClient.get<{ Id: number; Name: string }[]>('/vehicles/cargo/app-finder/makes', { params: { year } });
    return response.data;
  },

  async getCargoAppFinderModels(year: number, makeId: number): Promise<{ Id: number; Name: string }[]> {
    const response = await apiClient.get<{ Id: number; Name: string }[]>('/vehicles/cargo/app-finder/models', { params: { year, makeId } });
    return response.data;
  },

  async getCargoAppFinderVehicleOptions(year: number, makeId: number, modelId: number): Promise<{ Id: number; Name: string }[]> {
    const response = await apiClient.get<{ Id: number; Name: string }[]>('/vehicles/cargo/app-finder/options', { params: { year, makeId, modelId } });
    return response.data;
  },

  async getCargoAppFinderPartsData(vehicleId: number): Promise<any> {
    const response = await apiClient.get(`/vehicles/cargo/${vehicleId}/app-finder`);
    return response.data;
  },

  // ── Cargo Vehicle CRUD ──────────────────────────────────────────────────────

  async getCargoVehicles(makeId?: number, modelId?: number, page: number = 1): Promise<VehiclesResponse> {
    const params: Record<string, string | number> = { page };
    if (makeId)  params.makeId  = makeId;
    if (modelId) params.modelId = modelId;
    const response = await apiClient.get<VehiclesResponse>('/vehicles/cargo', { params });
    return response.data;
  },

  async getCargoVehicleById(id: number): Promise<any> {
    const response = await apiClient.get(`/vehicles/cargo/${id}`);
    return response.data;
  },

  async createCargoVehicle(data: any): Promise<{ id: number; message: string }> {
    const response = await apiClient.post<{ id: number; message: string }>('/vehicles/cargo', data);
    return response.data;
  },

  async updateCargoVehicle(id: number, data: any): Promise<{ message: string }> {
    const response = await apiClient.put<{ message: string }>(`/vehicles/cargo/${id}`, data);
    return response.data;
  },

  async deleteCargoVehicle(id: number): Promise<void> {
    await apiClient.delete(`/vehicles/cargo/${id}`);
  },

  async getCargoVehicleLookupData(): Promise<{ makes: any[] }> {
    const response = await apiClient.get<{ makes: any[] }>('/vehicles/cargo/lookup-data');
    return response.data;
  },

  // ── Cargo Part Application CRUD ────────────────────────────────────────────

  async getCargoPartApplicationByPartNumber(partNumber: string): Promise<{ Id: number; PartNumber: string } | null> {
    const response = await apiClient.get<{ Id: number; PartNumber: string }>('/vehicles/cargo/parts/by-number', {
      params: { partNumber },
      validateStatus: (s) => s === 200 || s === 404,
    });
    if (response.status === 404) return null;
    return response.data;
  },

  async getCargoPartApplication(partId: number): Promise<any> {
    const response = await apiClient.get(`/vehicles/cargo/parts/${partId}`);
    return response.data;
  },

  async updateCargoPartApplication(partId: number, data: any): Promise<void> {
    await apiClient.put(`/vehicles/cargo/parts/${partId}`, data);
  },

  async createCargoPartApplicationWithLink(vehicleId: number, data: any): Promise<{ partId: number }> {
    const response = await apiClient.post<{ partId: number }>(`/vehicles/cargo/${vehicleId}/applications/new`, data);
    return response.data;
  },

  async addCargoPartApplicationQuestion(partId: number, questionId: number, answerId: number): Promise<number> {
    const response = await apiClient.post<{ id: number }>(`/vehicles/cargo/parts/${partId}/questions`, { questionId, answerId });
    return response.data.id;
  },

  async deleteCargoPartApplicationQuestion(partId: number, qaId: number): Promise<void> {
    await apiClient.delete(`/vehicles/cargo/parts/${partId}/questions/${qaId}`);
  },

  // ── Cargo Part links ────────────────────────────────────────────────────────

  async getCargoApplications(vehicleId: number): Promise<{ Id: number; PartId: number; PartNumber: string; Extras?: string | null }[]> {
    const response = await apiClient.get<{ Id: number; PartId: number; PartNumber: string; Extras?: string | null }[]>(`/vehicles/cargo/${vehicleId}/applications`);
    return response.data;
  },

  async addCargoApplication(vehicleId: number, partNumber: string): Promise<{ id: number }> {
    const response = await apiClient.post<{ id: number }>(`/vehicles/cargo/${vehicleId}/applications`, { partNumber });
    return response.data;
  },

  async removeCargoApplication(vehicleId: number, cargoId: number): Promise<void> {
    await apiClient.delete(`/vehicles/cargo/${vehicleId}/applications/${cargoId}`);
  },

  async exportDashToExcel(makeIds: number[]): Promise<Blob> {
    const response = await apiClient.post('/vehicles/dash/export', { makeIds }, { responseType: 'blob' });
    return response.data as Blob;
  },

  async getPrintDashPartsListData(makeIds: number[]): Promise<VehicleExportDto[]> {
    const response = await apiClient.post<VehicleExportDto[]>('/vehicles/dash/print-parts-list', { makeIds });
    return response.data;
  },

  // ── Dash Cover App Finder cascade ───────────────────────────────────────────

  async getDashAppFinderYears(): Promise<number[]> {
    const response = await apiClient.get<number[]>('/vehicles/dash/app-finder/years');
    return response.data;
  },

  async getDashAppFinderMakes(year: number): Promise<{ Id: number; Name: string }[]> {
    const response = await apiClient.get<{ Id: number; Name: string }[]>('/vehicles/dash/app-finder/makes', { params: { year } });
    return response.data;
  },

  async getDashAppFinderModels(year: number, makeId: number): Promise<{ Id: number; Name: string }[]> {
    const response = await apiClient.get<{ Id: number; Name: string }[]>('/vehicles/dash/app-finder/models', { params: { year, makeId } });
    return response.data;
  },

  async getDashAppFinderVehicleOptions(year: number, makeId: number, modelId: number): Promise<{ Id: number; Name: string }[]> {
    const response = await apiClient.get<{ Id: number; Name: string }[]>('/vehicles/dash/app-finder/options', { params: { year, makeId, modelId } });
    return response.data;
  },

  async getDashAppFinderPartsData(vehicleId: number): Promise<any> {
    const response = await apiClient.get(`/vehicles/dash/${vehicleId}/app-finder`);
    return response.data;
  },

  // ── Dash Cover Vehicle CRUD ──────────────────────────────────────────────────

  async getDashVehicles(makeId?: number, modelId?: number, page: number = 1): Promise<VehiclesResponse> {
    const params: any = { page };
    if (makeId) params.makeId = makeId;
    if (modelId) params.modelId = modelId;
    const response = await apiClient.get<VehiclesResponse>('/vehicles/dash', { params });
    return response.data;
  },

  async getDashVehicleById(id: number): Promise<any> {
    const response = await apiClient.get(`/vehicles/dash/${id}`);
    return response.data;
  },

  async createDashVehicle(data: any): Promise<{ id: number; message: string }> {
    const response = await apiClient.post<{ id: number; message: string }>('/vehicles/dash', data);
    return response.data;
  },

  async updateDashVehicle(id: number, data: any): Promise<{ message: string }> {
    const response = await apiClient.put<{ message: string }>(`/vehicles/dash/${id}`, data);
    return response.data;
  },

  async deleteDashVehicle(id: number): Promise<void> {
    await apiClient.delete(`/vehicles/dash/${id}`);
  },

  async getDashVehicleLookupData(): Promise<{ makes: any[] }> {
    const response = await apiClient.get<{ makes: any[] }>('/vehicles/dash/lookup-data');
    return response.data;
  },

  // ── Dash Cover Part Application CRUD ────────────────────────────────────────

  async getDashPartApplicationByPartNumber(partNumber: string): Promise<{ Id: number; PartNumber: string } | null> {
    const response = await apiClient.get<{ Id: number; PartNumber: string }>('/vehicles/dash/parts/by-number', {
      params: { partNumber },
      validateStatus: (status) => status === 200 || status === 404,
    });
    return response.status === 404 ? null : response.data;
  },

  async getDashPartApplication(partId: number): Promise<any> {
    const response = await apiClient.get(`/vehicles/dash/parts/${partId}`);
    return response.data;
  },

  async updateDashPartApplication(partId: number, data: any): Promise<void> {
    await apiClient.put(`/vehicles/dash/parts/${partId}`, data);
  },

  async createDashPartApplicationWithLink(vehicleId: number, data: any): Promise<{ partId: number }> {
    const response = await apiClient.post<{ partId: number }>(`/vehicles/dash/${vehicleId}/applications/new`, data);
    return response.data;
  },

  async addDashPartApplicationQuestion(partId: number, questionId: number, answerId: number): Promise<number> {
    const response = await apiClient.post<{ id: number }>(`/vehicles/dash/parts/${partId}/questions`, { questionId, answerId });
    return response.data.id;
  },

  async deleteDashPartApplicationQuestion(partId: number, qaId: number): Promise<void> {
    await apiClient.delete(`/vehicles/dash/parts/${partId}/questions/${qaId}`);
  },

  // ── Dash Cover Part links ────────────────────────────────────────────────────

  async getDashApplications(vehicleId: number): Promise<{ Id: number; PartId: number; PartNumber: string; Extras?: string | null }[]> {
    const response = await apiClient.get<{ Id: number; PartId: number; PartNumber: string; Extras?: string | null }[]>(`/vehicles/dash/${vehicleId}/applications`);
    return response.data;
  },

  async addDashApplication(vehicleId: number, partNumber: string): Promise<{ id: number }> {
    const response = await apiClient.post<{ id: number }>(`/vehicles/dash/${vehicleId}/applications`, { partNumber });
    return response.data;
  },

  async removeDashApplication(vehicleId: number, dashId: number): Promise<void> {
    await apiClient.delete(`/vehicles/dash/${vehicleId}/applications/${dashId}`);
  },
};
